import { useCallback } from 'react'
import { useSummaryById, useSummaryDate } from '@renderer/chat/hooks'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { useSummaryContext } from './use-summary-context'
import { useDebouncedSave } from './use-debounced-save'

export function useSummaryQuickAdd(
  edited: string | undefined,
  setEdited: (edited: string | undefined) => void,
  onSaveEdits: (updated?: string) => Promise<void>,
) {
  const { summary } = useSummaryContext()
  const { data: fullSummary } = useSummaryById(summary.id)
  const { isToday, dayLabel } = useSummaryDate(summary)
  const { debouncedSave, isSaving } = useDebouncedSave(async (content: string) => {
    await onSaveEdits(content)
  })

  const addItemToSection = useCallback(
    (sectionName: string, itemText: string) => {
      if (!itemText.trim()) return

      const md = edited ?? (fullSummary?.summary || '')
      const lines = md.split('\n')

      // Find the section header (case-insensitive)
      const sectionRegex = new RegExp(`^##\\s+${sectionName}\\s*$`, 'i')
      const sectionIndex = lines.findIndex((line) => sectionRegex.test(line))

      // Create the new list item
      const newItem = `[ ] ${itemText.trim()}`

      let newMarkdown: string

      if (sectionIndex >= 0) {
        // Append to the very end
        newMarkdown = md + '\n' + newItem
      } else {
        // Section doesn't exist, append to end
        // Add a newline before appending if the last line isn't empty
        if (md.trim().length > 0 && !md.endsWith('\n\n')) {
          newMarkdown = md.trim() + '\n\n' + newItem
        } else {
          newMarkdown = md.trim() + '\n' + newItem
        }
      }

      // Update edited state
      setEdited(newMarkdown)

      // Save with debounce
      debouncedSave(newMarkdown)

      AmplitudeRenderer.trackEvent('Summary Action', {
        type: 'Task Add',
        timePeriod: isToday ? 'Today' : dayLabel,
      })
    },
    [edited, fullSummary?.summary, setEdited, debouncedSave, isToday, dayLabel],
  )

  return { addItemToSection, isSaving }
}
